import * as apiActions from 'aster-foundation/core/astercore/nps/redux/actions';
import Image from 'next/image';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import Button from 'react-bootstrap/Button';
import { IC_CLOSE } from '../../Assets/images';
import { errorHandling, checkRatingMatch } from '@common/Utility/common-utils';
import { numbers } from '@common/Utility/constants';
import { InputFieldControl } from '../Forms';
import ButtonChip from './ButtonChip';
import CheckboxGroup from './CheckboxGroup';
import { NpsFeedbackStyled } from './NpsFeedbackStyled';
import NpsStarRating from './NpsStarRating';
import { EventEmitter } from '@common/Utility/eventEmitter';

const NpsFeedback = ({ EventType, OrderId, setShowNps }) => {
  const dispatch = useDispatch();
  const userId = useSelector((state) => state?.astercore?.checkUserExist?.data?.entry[0]?.resource?.id);

  const [ratingValue, setRatingValue] = React.useState(null);
  const [inputEvents, setInputEvents] = React.useState(null);
  const [buttonChipData, setButtonChipData] = React.useState(null);
  const [checkboxGroupData, setCheckboxGroupData] = React.useState(null);
  const [starRatingData, setStarRatingData] = React.useState(null);
  const [eventData, setEventData] = React.useState([]);
  const [showCurrentForm, setShowCurrentForm] = React.useState(false);
  const [apiResponses, setApiResponses] = React.useState([]);
  const [callApi, setCallApi] = React.useState(false);
  const [npmCommonGetApiResponse, setNpmCommonGetApiResponse] = React.useState(null)

  const findMatchingChild = ({ ratingValue, getResponseData, setApiResponses }) => {

    const rating = ratingValue?.answer;
    const parentChildren = getResponseData?.body?.data?.[0]?.children;
  
    if (rating === undefined || !Array.isArray(parentChildren)) {
      return;
    }
  
    const parentEvent = getResponseData?.body?.data?.[0]?.event;
    const parentQuestionId = parentEvent?.questions?.[0]?.questionId;
  
    const checkChildForMatch = (child) => {
      if (child?.questionId !== parentQuestionId) {
        return false;
      }
  
      if (child?.children && child?.children.length === 0) {
        const config = child?.config;
        const childRatingValue = parseFloat(config?.value);
  
        const isMatch = checkRatingMatch(rating, childRatingValue, config?.relation);
  
        if (isMatch) {
          setApiResponses((prevResponses) => {
            if (prevResponses?.length > 1) {
              return [prevResponses[0], child.event];
            } else {
              return [...prevResponses, child.event];
            }
          });
          return true;
        }
        return false;
      }
  
      if (child?.children && child?.children.length > 0) {
        for (const grandChild of child.children) {
          if (checkChildForMatch(grandChild)) {
            return true;
          }
        }
      }
  
      return false;
    };
  
    for (const child of parentChildren) {
      if (checkChildForMatch(child)) {
        return;
      } 
      handleSubmit();
    }
  };

  useEffect(() => {
    const updatedArray = [...apiResponses];

    updatedArray?.forEach((item) => {
      if (ratingValue) {
        const index = updatedArray?.findIndex((arrayItem) => arrayItem?.eventId === ratingValue?.eventId);
        if (index !== -1) {
          const question = updatedArray[index]?.questions?.find(
            (questionItem) => questionItem?.questionId === ratingValue?.questionId,
          );
          if (question) {
            if (!question?.answer) {
              question.answer = [];
            }
            question?.answer?.push(ratingValue?.answer);
          }
        }
      }

      if (inputEvents) {
        const index = updatedArray?.findIndex((arrayItem) => arrayItem?.eventId === inputEvents?.eventId);
        if (index !== -1) {
          const question = updatedArray[index]?.questions?.find(
            (questionItem) => questionItem?.questionId === inputEvents?.questionId,
          );
          if (question) {
            if (!question?.answers) {
              question.answer = [];
            }
            question?.answer?.push(inputEvents?.answer);
          }
        }
      }

      if (buttonChipData) {
        const index = updatedArray?.findIndex((arrayItem) => arrayItem?.eventId === buttonChipData?.eventId);
        if (index !== -1) {
          const question = updatedArray[index]?.questions.find(
            (questionItem) => questionItem?.questionId === buttonChipData?.questionId,
          );
          if (question) {
            if (!question?.answers) {
              question.answer = [];
            }
            question?.answer?.push(buttonChipData?.answer);
          }
        }
      }

      if (checkboxGroupData) {
        const index = updatedArray?.findIndex((arrayItem) => arrayItem?.eventId === checkboxGroupData?.eventId);
        if (index !== -1) {
          const question = updatedArray[index]?.questions.find(
            (questionItem) => questionItem?.questionId === checkboxGroupData?.questionId,
          );
          if (question) {
            if (!question?.answers) {
              question.answer = [];
            }
            question?.answer?.push(checkboxGroupData?.answer);
          }
        }
      }

      if (starRatingData) {
        const index = updatedArray?.findIndex((arrayItem) => arrayItem?.eventId === starRatingData?.eventId);
        if (index !== -1) {
          const question = updatedArray[index]?.questions.find(
            (questionItem) => questionItem?.questionId === starRatingData?.questionId,
          );
          if (question) {
            if (!question?.answers) {
              question.answer = [];
            }
            question?.answer?.push(starRatingData?.answer);
          }
        }
      }
      setEventData(updatedArray);
    });
  }, [buttonChipData, checkboxGroupData, starRatingData, ratingValue, showCurrentForm, inputEvents]);

  const npmCommonGetApi = (param) => {
    const params = { eventName: param, userId };
    dispatch(
      apiActions.NPSCommonGet({
        params,
        onSuccess: (response) => {
          setNpmCommonGetApiResponse(response);
          setApiResponses((prevResponses) => [...prevResponses, response?.body?.data[0]?.event]);
        },
        onFailure: (err) => {
          setShowNps(false);
          errorHandling(err, true);
        },
      }),
    );
  };

  React.useEffect(() => {
    const updatedPayloadData = [...eventData];
    if (updatedPayloadData?.length > 0) {
      const lastIndex = updatedPayloadData?.length - 1;
      updatedPayloadData[lastIndex].show = showCurrentForm;
      setEventData(updatedPayloadData);
    }
  }, [showCurrentForm, apiResponses]);

  const npsCommonPostApi = (newData, skip = false) => {
    const params = newData;
      dispatch(
        apiActions.NPSCommonPost({
          params,
          onSuccess: (response) => {
            setCallApi(true);
            setShowNps(false);
            if(!skip){
              EventEmitter.dispatch('setToastControlMessage', { message: response?.message });
            }
          },
          onFailure: (err) => {
            errorHandling(err);
          },
        }),
      );
  };

  React.useEffect(() => {
    const rating = [];
    apiResponses?.forEach((item) => {
      item?.questions?.forEach((question) => {
        if (question?.questionType === 'RATING_TO_10') {
          rating.push(item);
        }
      });
    });
    setRatingValue(rating);
  }, [apiResponses]);

  React.useEffect(() => {
    npmCommonGetApi(EventType);
  }, [EventType]);

  const handleSubmit = (skipped = false) => {
    eventData?.forEach(event => {
      const { eventId, questions } = event || {};  
  
      const payload = {
        eventId,
        userId: userId,
        orderId: OrderId,
        answers: questions.map(question => {
          const { questionId, questionType, answer } = question || {};
  
          let answerValue = null;
  
          if (questionType === "RATING_TO_10" && Array.isArray(answer)) {
            answerValue = answer?.[0];
          }
          else if (questionType === "MULTI_SELECT" && Array.isArray(answer)) {
            answerValue = answer.join(",");
          }
          else {
            answerValue = answer?.[0] || null;
          }
  
          return {
            questionId,
            answer: answerValue || null
          };
        })
      };
  
      npsCommonPostApi(payload, skipped);
    });
    setShowNps(false);
  };

  React.useEffect(() => {

  findMatchingChild({
    ratingValue,
    getResponseData: npmCommonGetApiResponse,
    setApiResponses
  });

  if (apiResponses.length > 1) {
    setCallApi(true);
  }
}, [ratingValue]);

  return (
    <NpsFeedbackStyled> 
      <div className="modalHeader">
        <span className="title">{apiResponses?.[0]?.title}</span>
        <button type="button" className="closeBtn" onClick={() => {handleSubmit(true);}}>
          <Image src={IC_CLOSE.src} height={13} width={13} alt="close" />
        </button>
      </div>
      <div className="modalBody">
        {apiResponses &&
          apiResponses.length > 0 &&
          apiResponses?.map((item, index) => (
            <section className="npsSection" key={item?.eventId}>
              {index !== 0 && item?.title && <div className="npsTitle">{item?.title}</div>}
              {item?.description && <div className="npsSubTtitle">{item?.description}</div>}
              {item?.questions?.map((question) => (
                <div className={`npsQuestionWrapper ${question?.questionType === "SINGLE_SELECT" ? "chipButton" : ""}`} key={question?.questionId}>
                  {(() => {
                    switch (question?.questionType) {
                      case "RATING_TO_10":
                        return (
                          <div className="bottomBorder">
                            <div className="numberRating">
                              {numbers.map((number) => (
                                <div
                                  key={number}
                                  className={`number ${question?.answer?.[0] === number ? 'selected' : ''}`}
                                  onClick={() => {

                                    question.answer = [];
                                  
                                    if(!item?.show){
                                      setRatingValue({
                                        questionId: question?.questionId,
                                        answer: number,
                                        eventId: item?.eventId
                                      })
                                    }

                                  }}
                                >
                                  {number}
                                </div>
                              ))}
                            </div>
                            <div className="like">
                              <span>Not at all likely</span>
                              <span>Extremely likely</span>
                            </div>
                          </div>
                        );
                      case 'SINGLE_SELECT':
                        return (
                          <ButtonChip
                            Question={question}
                            OptionList={question?.options}
                            customClass=""
                            setButtonChipData={setButtonChipData}
                            eveId={item?.eventId}
                            disabled={item?.show}
                          />
                        );
                      case 'MULTI_SELECT':
                        return (
                          <CheckboxGroup
                            questions={question}
                            setCheckboxGroupData={setCheckboxGroupData}
                            eveId={item?.eventId}
                            disabled={item?.show}
                          />
                        );
                      case 'STAR_RATING':
                        return (
                          <NpsStarRating
                            question={question}
                            setStarRatingData={setStarRatingData}
                            eveId={item?.eventId}
                            disabled={item?.show}
                          />
                        );
                      case 'DESCRIPTION':
                        return (
                          <>
                            <InputFieldControl
                              errorMessage="Maximum 100 characters allowed"
                              placeholder={`${question?.questionTitle} (Optional)`}
                              isTextArea
                              isLabel
                              label={`${question?.questionTitle} (Optional)`}
                              valueType={question?.answer?.[0] || ""}
                              data-test="inputControl1"
                              onChange={(e) => {
                                setInputEvents({
                                  questionId: question?.questionId,
                                  answer: e.target.value,
                                  eventId: item?.eventId,
                                });
                              }}
                              customClass="npsTextArea"
                              maxLength={100}
                              eveId={item?.eventId}
                              disabled={item?.show}
                            />
                          </>
                        );
                      default:
                        return null;
                    }
                  })()}
                </div>
              ))}
            </section>
          ))}
        {callApi && (
          <section className="npsSection modalFooter">
            <div className="npsSubmit">
              <Button key="Submit" variant="primary" size="sm" onClick={()=>{handleSubmit();}}>
                Submit
              </Button>
            </div>
          </section>
        )}
      </div>
  </NpsFeedbackStyled>
  );
};

NpsFeedback.defaultProps= {
  EventType: '',
  OrderId: '',
  setShowNps: ()=>{},
};

NpsFeedback.propTypes= {
  EventType: PropTypes.string,
  OrderId: PropTypes.string,
  setShowNps: PropTypes.func,
};

export default NpsFeedback;
